import jsPDF from "jspdf"
import formatNumberToString from "./formatNumber"
import removeTimezone from "./removeTimezone"

// ─── Types ────────────────────────────────────────────────────────────────────

interface StatementPayment {
    date: string
    amount: number
    amount_tickets: number
    remarks?: string | null
    payment_method?: string | null
}

interface StatementMovement {
    kind: 'traspaso' | 'general_payment'
    date: string
    balance: 'positive' | 'negative'
    // traspaso fields
    passenger_name?: string | null
    passenger_nationality?: string | null
    tour_name?: string | null
    traspass_amount?: number
    tickets_amount?: number
    revision_status?: string | null
    revision_ticket_status?: string | null
    revision_notes?: string | null
    revision_chat_source?: string | null
    payments?: StatementPayment[]
    // general payment fields
    amount?: number
    amount_tickets?: number
    remarks?: string | null
    payment_method?: string | null
}

interface StatementSummary {
    reservations_balance: number
    retraspaso_balance: number
    payments_balance: number
    total: number
}

export interface AccountStatement {
    counterpart_agency: { id: string; name: string; email?: string }
    from: string | null
    to: string | null
    summary: StatementSummary | null
    positive: StatementMovement[]
    negative: StatementMovement[]
}

// ─── Labels (mirrors CreateRevision.tsx) ──────────────────────────────────────

const TICKET_STATUS_LABELS: Record<string, string> = {
    included_in_account: 'Incluida en cuenta',
    paid_cash: 'Pagada en efectivo',
    paid_transfer: 'Pagada por transferencia',
    paid_at_agency: 'Pagada en agencia',
    passenger_pays: 'Pasajero lleva efectivo',
    pending_payment: 'Pendiente de pago',
    qr_sent: 'QR enviado por chat',
    no_entrance: 'Sin entrada (tour libre)',
    not_determined: 'No determinado'
}

const REVISION_STATUS_LABELS: Record<string, { label: string; color: [number, number, number]; bg: [number, number, number] }> = {
    verified: { label: 'Verificado', color: [12, 68, 124], bg: [230, 241, 251] },
    pending: { label: 'Pendiente', color: [133, 79, 11], bg: [250, 238, 218] },
    no_chat_backup: { label: 'Sin respaldo de chat', color: [95, 94, 90], bg: [241, 239, 232] },
    error: { label: 'Error', color: [121, 31, 31], bg: [252, 235, 235] }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function money(value?: number | null): string {
    const n = Number(value) || 0
    return `$${formatNumberToString(n)}`
}

function fmtDate(value?: string | null): string {
    if (!value) return ''
    try {
        return removeTimezone(value.toString())
    } catch {
        return ''
    }
}

const PAGE_MARGIN = 14
const COL_GAP = 8
const BOX_GAP = 4
const ROW_LINE = 4.6

// Measures the height (mm) the box for a movement will take, given a content width.
function measureMovementHeight(doc: jsPDF, item: StatementMovement, contentWidth: number): number {
    if (item.kind === 'general_payment') {
        const lines = item.remarks ? doc.splitTextToSize(`"${item.remarks}"`, contentWidth - 6).length : 0
        return 6 + ROW_LINE + (lines * ROW_LINE) + 4
    }

    let h = 6 // top padding
    h += ROW_LINE // name + date row
    const tourLines = item.tour_name ? doc.splitTextToSize(item.tour_name, contentWidth - 6).length : 1
    h += tourLines * ROW_LINE
    h += ROW_LINE // amounts row
    h += 2 // separator spacing
    const payments = item.payments || []
    if (payments.length > 0) {
        h += ROW_LINE // "Abonos registrados" label
        h += payments.length * ROW_LINE
    } else {
        h += ROW_LINE // "Sin abonos registrados"
    }
    h += 4 // bottom padding
    return h
}

function drawMovement(doc: jsPDF, item: StatementMovement, x: number, y: number, width: number, height: number) {
    const padX = 3
    const contentX = x + padX
    const contentWidth = width - padX * 2

    if (item.kind === 'general_payment') {
        doc.setDrawColor(180, 180, 180)
        doc.setLineDashPattern([1.2, 1], 0)
        doc.setFillColor(245, 245, 242)
        doc.roundedRect(x, y, width, height, 1.5, 1.5, 'FD')
        doc.setLineDashPattern([], 0)

        let cy = y + 5
        doc.setFont('Roboto', 'normal')
        doc.setFontSize(8.5)
        doc.setTextColor(95, 94, 90)
        const methodLabel = item.payment_method ? ` · ${item.payment_method}` : ''
        doc.text(`Pago general · ${fmtDate(item.date)}${methodLabel}`, contentX, cy)

        doc.setFont('Roboto', 'bold')
        doc.setTextColor(20, 20, 20)
        const total = (Number(item.amount) || 0) + (Number(item.amount_tickets) || 0)
        doc.text(money(total), x + width - padX, cy, { align: 'right' })

        if (item.remarks) {
            cy += ROW_LINE
            doc.setFont('Roboto', 'normal')
            doc.setFontSize(8)
            doc.setTextColor(136, 134, 128)
            const lines = doc.splitTextToSize(`"${item.remarks}"`, contentWidth)
            doc.text(lines, contentX, cy)
        }
        return
    }

    // ── traspaso (regular or re-traspaso — rendered identically) ──
    doc.setDrawColor(220, 218, 210)
    doc.setFillColor(255, 255, 255)
    doc.roundedRect(x, y, width, height, 1.5, 1.5, 'FD')

    let cy = y + 5

    // Name + date  |  revision badge
    doc.setFont('Roboto', 'bold')
    doc.setFontSize(9.5)
    doc.setTextColor(20, 20, 20)
    const namePart = item.passenger_name || 'Pasajero'
    doc.text(namePart, contentX, cy)
    const nameWidth = doc.getTextWidth(namePart)

    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(136, 134, 128)
    doc.text(`  ·  ${fmtDate(item.date)}`, contentX + nameWidth, cy)

    if (item.revision_status && item.revision_status !== 'pending') {
        const rev = REVISION_STATUS_LABELS[item.revision_status]
        if (rev) {
            doc.setFontSize(7)
            doc.setFont('Roboto', 'bold')
            const label = rev.label
            const tw = doc.getTextWidth(label) + 4
            const bx = x + width - padX - tw
            doc.setFillColor(rev.bg[0], rev.bg[1], rev.bg[2])
            doc.roundedRect(bx, cy - 3.2, tw, 4.4, 1.5, 1.5, 'F')
            doc.setTextColor(rev.color[0], rev.color[1], rev.color[2])
            doc.text(label, bx + 2, cy)
        }
    }

    // Tour
    cy += ROW_LINE
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(95, 94, 90)
    const tourLines: string[] = item.tour_name ? doc.splitTextToSize(item.tour_name, contentWidth - 6) : ['']
    doc.text(tourLines, contentX, cy)
    cy += (tourLines.length - 1) * ROW_LINE

    // Amounts row + ticket status label
    cy += ROW_LINE
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(95, 94, 90)
    doc.text('Traspass', contentX, cy)
    doc.setFont('Roboto', 'bold')
    doc.setTextColor(20, 20, 20)
    const traspassLabel = `Traspass ${money(item.traspass_amount)}`
    doc.text(money(item.traspass_amount), contentX + doc.getTextWidth('Traspass ') , cy)

    const ticketsX = contentX + doc.getTextWidth(traspassLabel) + 8
    doc.setFont('Roboto', 'normal')
    doc.setTextColor(95, 94, 90)
    doc.text('Entradas', ticketsX, cy)
    doc.setFont('Roboto', 'bold')
    doc.setTextColor(20, 20, 20)
    doc.text(money(item.tickets_amount), ticketsX + doc.getTextWidth('Entradas '), cy)

    if (item.revision_ticket_status) {
        const tLabel = TICKET_STATUS_LABELS[item.revision_ticket_status] || item.revision_ticket_status
        doc.setFont('Roboto', 'normal')
        doc.setFontSize(7)
        const tw = doc.getTextWidth(tLabel) + 4
        const bx = x + width - padX - tw
        doc.setFillColor(234, 243, 222)
        doc.roundedRect(bx, cy - 3.2, tw, 4.4, 1.5, 1.5, 'F')
        doc.setTextColor(59, 109, 17)
        doc.text(tLabel, bx + 2, cy)
    }

    // Separator + abonos
    cy += 3
    doc.setDrawColor(225, 223, 216)
    doc.setLineDashPattern([0.8, 0.8], 0)
    doc.line(contentX, cy, x + width - padX, cy)
    doc.setLineDashPattern([], 0)

    cy += ROW_LINE
    const payments = item.payments || []
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(136, 134, 128)
    if (payments.length === 0) {
        doc.text('Sin abonos registrados', contentX, cy)
    } else {
        doc.text('Abonos registrados', contentX, cy)
        payments.forEach(p => {
            cy += ROW_LINE
            const concept = (p.amount && p.amount_tickets)
                ? 'tour y entradas'
                : (p.amount_tickets ? 'entradas' : 'tour')
            const methodLabel = p.payment_method ? ` · ${p.payment_method}` : ''
            doc.setFont('Roboto', 'normal')
            doc.setTextColor(136, 134, 128)
            doc.text(`${fmtDate(p.date)} · ${concept}${methodLabel}`, contentX + 2, cy)
            doc.setFont('Roboto', 'bold')
            doc.setTextColor(70, 70, 70)
            const total = (Number(p.amount) || 0) + (Number(p.amount_tickets) || 0)
            doc.text(money(total), x + width - padX, cy, { align: 'right' })
        })
    }
}

// ─── Main entry point ─────────────────────────────────────────────────────────

export default async function generateAccountStatementPdf(
    statement: AccountStatement
) {
    const doc = new jsPDF({ orientation: 'landscape' })
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    try {
        doc.addFont('/fonts/Roboto-Regular.ttf', 'Roboto', 'normal')
        doc.addFont('/fonts/Roboto-Medium.ttf', 'Roboto', 'medium')
        doc.addFont('/fonts/Roboto-Bold.ttf', 'Roboto', 'bold')
    } catch {
        doc.setFont('helvetica')
    }

    try {
        doc.addImage('/assets/logo.png', 'PNG', PAGE_MARGIN, 8, 18, 18)
    } catch (error) {
        console.log('Could not load logo:', error)
    }

    const counterpartName = statement.counterpart_agency?.name || 'Agencia'
    const periodLabel = (statement.from && statement.to)
        ? `${fmtDate(statement.from)} – ${fmtDate(statement.to)}`
        : 'Histórico completo'

    doc.setFont('Roboto', 'bold')
    doc.setFontSize(13)
    doc.setTextColor(4, 44, 83)
    doc.text('MOLANTUR', PAGE_MARGIN + 24, 14)
    doc.setFontSize(10)
    doc.text('ATACAMA TOURS', PAGE_MARGIN + 24, 19.5)

    doc.setFont('Roboto', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(95, 94, 90)
    doc.text(`Estado de cuenta — ${counterpartName} · ${periodLabel}`, PAGE_MARGIN + 60, 16)

    doc.setFontSize(8)
    doc.setTextColor(136, 134, 128)
    doc.text(`Generado el ${format_today()}`, pageWidth - PAGE_MARGIN, 16, { align: 'right' })

    // ── Summary ──
    const summary = statement.summary
    const summaryY = 30

    // Saldo final — full-width banner
    const bannerH = 13
    doc.setFillColor(245, 245, 242)
    doc.roundedRect(PAGE_MARGIN, summaryY, pageWidth - PAGE_MARGIN * 2, bannerH, 2, 2, 'F')
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(136, 134, 128)
    doc.text('Saldo final', PAGE_MARGIN + 5, summaryY + 5.5)
    const saldoPositive = (summary?.total || 0) >= 0
    doc.setFont('Roboto', 'bold')
    doc.setFontSize(14)
    doc.setTextColor(saldoPositive ? 39 : 121, saldoPositive ? 109 : 31, saldoPositive ? 10 : 31)
    doc.text(money(summary?.total), PAGE_MARGIN + 5, summaryY + 10.5)
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(136, 134, 128)
    doc.text(saldoPositive ? `A favor de Molantur` : `A favor de ${counterpartName}`, pageWidth - PAGE_MARGIN - 5, summaryY + 8, { align: 'right' })

    // Breakdown grid — 2 rows (Molantur / counterpart) x 3 cards (Traspasos · Entradas · Pagos)
    function sumMovements(movements: StatementMovement[]) {
        let traspasos = 0, entradas = 0, pagos = 0
        movements.forEach(m => {
            if (m.kind === 'traspaso') {
                traspasos += Number(m.traspass_amount) || 0
                entradas += Number(m.tickets_amount) || 0
                ;(m.payments || []).forEach(p => { pagos += (Number(p.amount) || 0) + (Number(p.amount_tickets) || 0) })
            } else {
                pagos += (Number(m.amount) || 0) + (Number(m.amount_tickets) || 0)
            }
        })
        return { traspasos, entradas, pagos }
    }
    const positiveTotals = sumMovements(statement.positive)
    const negativeTotals = sumMovements(statement.negative)

    const gridY = summaryY + bannerH + 5
    const gridGap = 6
    const cardW = (pageWidth - PAGE_MARGIN * 2 - gridGap * 2) / 3
    const cardH = 13
    const rowGap = 5

    function drawBreakdownRow(y: number, rowLabel: string, totals: { traspasos: number; entradas: number; pagos: number }) {
        doc.setFont('Roboto', 'bold')
        doc.setFontSize(7.5)
        doc.setTextColor(95, 94, 90)
        doc.text(`Hacia ${rowLabel}`, PAGE_MARGIN, y - 1.5)

        const items = [
            { label: 'Traspasos', value: totals.traspasos },
            { label: 'Entradas', value: totals.entradas },
            { label: 'Pagos', value: totals.pagos }
        ]
        items.forEach((item, idx) => {
            const cx = PAGE_MARGIN + idx * (cardW + gridGap)
            doc.setFillColor(248, 248, 246)
            doc.roundedRect(cx, y, cardW, cardH, 2, 2, 'F')
            doc.setFont('Roboto', 'normal')
            doc.setFontSize(7.5)
            doc.setTextColor(136, 134, 128)
            doc.text(item.label, cx + 5, y + 5.5)
            doc.setFont('Roboto', 'bold')
            doc.setFontSize(10)
            doc.setTextColor(20, 20, 20)
            doc.text(money(item.value), cx + 5, y + 11)
        })
    }

    drawBreakdownRow(gridY, 'Molantur', positiveTotals)
    drawBreakdownRow(gridY + cardH + rowGap + 4, counterpartName, negativeTotals)

    // ── Two columns ──
    const startY = gridY + (cardH + rowGap + 4) * 2 + 6
    const colWidth = (pageWidth - PAGE_MARGIN * 2 - COL_GAP) / 2
    const xLeft = PAGE_MARGIN
    const xRight = PAGE_MARGIN + colWidth + COL_GAP
    const maxY = pageHeight - PAGE_MARGIN

    function drawColumnHeader(x: number, y: number, title: string, color: [number, number, number], bg: [number, number, number]) {
        doc.setFillColor(bg[0], bg[1], bg[2])
        doc.roundedRect(x, y, colWidth, 9, 2, 2, 'F')
        doc.setFont('Roboto', 'bold')
        doc.setFontSize(9.5)
        doc.setTextColor(color[0], color[1], color[2])
        doc.text(title, x + 4, y + 6)
    }

    let posIdx = 0
    let negIdx = 0
    let firstPage = true

    while (posIdx < statement.positive.length || negIdx < statement.negative.length || firstPage) {
        let yLeft = startY
        let yRight = startY

        if (!firstPage) {
            yLeft = PAGE_MARGIN
            yRight = PAGE_MARGIN
        }
        firstPage = false

        drawColumnHeader(xLeft, yLeft, 'A favor de Molantur', [8, 80, 65], [225, 245, 238])
        drawColumnHeader(xRight, yRight, `A favor de ${counterpartName}`, [121, 31, 31], [252, 235, 235])
        yLeft += 9 + BOX_GAP
        yRight += 9 + BOX_GAP

        let drewSomething = false

        while (posIdx < statement.positive.length) {
            const item = statement.positive[posIdx]
            const h = measureMovementHeight(doc, item, colWidth)
            if (yLeft + h > maxY) break
            drawMovement(doc, item, xLeft, yLeft, colWidth, h)
            yLeft += h + BOX_GAP
            posIdx++
            drewSomething = true
        }

        while (negIdx < statement.negative.length) {
            const item = statement.negative[negIdx]
            const h = measureMovementHeight(doc, item, colWidth)
            if (yRight + h > maxY) break
            drawMovement(doc, item, xRight, yRight, colWidth, h)
            yRight += h + BOX_GAP
            negIdx++
            drewSomething = true
        }

        const remaining = posIdx < statement.positive.length || negIdx < statement.negative.length
        if (remaining) {
            doc.addPage()
        } else if (!drewSomething && posIdx === 0 && negIdx === 0) {
            doc.setFont('Roboto', 'normal')
            doc.setFontSize(10)
            doc.setTextColor(136, 134, 128)
            doc.text('No hay movimientos registrados en este período.', pageWidth / 2, startY + 12, { align: 'center' })
            break
        } else {
            break
        }
    }

    // Footer
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFont('Roboto', 'normal')
        doc.setFontSize(7.5)
        doc.setTextColor(170, 168, 160)
        doc.text(`Generado por Traspax · página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 6, { align: 'center' })
    }

    const fileSafeName = counterpartName.toString().replace(/[^a-z0-9]+/gi, '_')
    doc.save(`estado_cuenta_${fileSafeName}.pdf`)
}

function format_today(): string {
    const d = new Date()
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}
